import type {
  LeadFlowBlockPresentation,
  LeadFlowDocument,
  LeadFlowStepDefinition,
} from "../types";

import { getProgress } from "./get-progress";

type LeadFlowStepLayoutOptions = {
  currentStep: LeadFlowStepDefinition;
  currentStepIndex: number;
  /** The first step, rendered in the page rather than on a surface. */
  isInContentSlot: boolean;
  isShowingThankYou: boolean;
  isTakingOverSection: boolean;
  presentation: LeadFlowDocument["presentation"];
  steps: LeadFlowStepDefinition[];
  surface: LeadFlowBlockPresentation["surface"];
};

function hasQuestionColumn(
  step: LeadFlowStepDefinition,
  canShowPanel: boolean,
): boolean {
  return (
    canShowPanel &&
    step._type === "leadFlowStep" &&
    step.layout === "question-with-panel" &&
    (step.infoPanel?.content?.length ?? 0) > 0
  );
}

/**
 * What the current step shows around its items: the progress indicator,
 * the info panel, and which of the two owns Back.
 */
function getStepLayout({
  currentStep,
  currentStepIndex,
  isInContentSlot,
  isShowingThankYou,
  isTakingOverSection,
  presentation,
  steps,
  surface,
}: LeadFlowStepLayoutOptions) {
  const progressIndicator = presentation?.progressIndicator ?? "default";
  const progress = getProgress(steps, currentStepIndex);
  // Interstitials aren't counted, so one before the first question step
  // (a launcher's opening step on a surface) has no progress to show. The
  // in-page first step also hides progress unless the flow opts in.
  const showProgress =
    !isShowingThankYou &&
    progress.position > 0 &&
    (!isInContentSlot || presentation?.showProgressOnFirstStep === true);
  const canShowPanel = !isInContentSlot && surface !== "sheet";

  return {
    canShowPanel,
    progress,
    progressIndicator,
    // The progress indicator sits outside the step transition, in the
    // question column, so changing steps updates it in place instead of
    // remounting it.
    sharesQuestionColumn:
      isTakingOverSection &&
      showProgress &&
      hasQuestionColumn(currentStep, canShowPanel),
    showProgress,
    // The `default` indicator carries its own Back link.
    stepShowsBack: !(showProgress && progressIndicator === "default"),
  };
}

export { getStepLayout };
